import React, { FC, useMemo } from "react";
import { Box, useDevice, View } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import "@lookiero/sty-psp-ui";
import { Button, Layout as UiLayout, Text } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Shape } from "../../../components/shape/Shape";
import { useStaticInfo } from "../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../i18n/i18n";
import { ReturnConfirmationHeader } from "../header/returnConfirmationHeader/ReturnConfirmationHeader";
import { Layout } from "../layout/Layout";
import { style as returnConfirmationStyle } from "./ReturnConfirmation.styles";

interface ReturnConfirmationProps {
  readonly layout: UiLayout;
  readonly onPressExploreYourLooks: () => void;
  readonly onPressShowReturnInstructions: () => void;
  readonly onClose: () => void;
}

const ReturnConfirmation: FC<ReturnConfirmationProps> = ({
  layout,
  onPressExploreYourLooks,
  onPressShowReturnInstructions,
  onClose,
}) => {
  const { screen } = useDevice();
  const { tradename } = useStaticInfo();

  const returnedOrderHeaderText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.RETURNED_ORDER_HEADER,
  });
  const returnedOrderBodyFirstText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.RETURNED_ORDER_BODY_FIRST,
  });
  const returnedOrderBodySecondText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.RETURNED_ORDER_BODY_SECOND,
  });
  const returnedOrderInstructionsCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.RETURNED_ORDER_INSTRUCTIONS_CTA,
  });
  const returnedOrderMoreLooksCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.RETURNED_ORDER_MORE_LOOKS_CTA,
  });

  const style = useMemo(() => returnConfirmationStyle(), []);

  return (
    <Layout footer={null} header={<ReturnConfirmationHeader onClose={onClose} />} layout={layout}>
      <View style={[style.wrapper, screen.L ? style.wrapperLarge : style.wrapperSmall]} wide>
        {tradename === Tradename.LOOKIERO && <Shape type="doubleHalf" />}
        <Box size={{ L: "1/3", M: "2/3" }} style={style.contentWrapper} testID="return-confirmation">
          <View style={[style.content, screen.L ? style.contentLarge : style.contentSmall]}>
            <View style={screen.L ? undefined : style.infoSmall}>
              <Text level={3} style={style.title} testID="home-title" variant="heading">
                {returnedOrderHeaderText}
              </Text>
              <Text style={style.bodyText}>{returnedOrderBodyFirstText}</Text>
              <Text style={style.bodyTextSecondary}>{returnedOrderBodySecondText}</Text>
            </View>
            <View style={[style.actions, screen.L ? style.actionsLarge : style.actionsSmall]}>
              <Button
                testID="return-instructions-button"
                variant="secondary"
                style={{
                  button: [style.button, screen.L ? style.instructions : style.instructionsSmall],
                }}
                onPress={onPressShowReturnInstructions}
              >
                {returnedOrderInstructionsCtaText}
              </Button>
              <Button
                testID="explore-your-looks-button"
                variant="primary"
                style={{
                  button: [style.button, screen.L ? style.goHome : undefined],
                }}
                onPress={onPressExploreYourLooks}
              >
                {returnedOrderMoreLooksCtaText}
              </Button>
            </View>
          </View>
        </Box>
        {tradename === Tradename.LOOKIERO && <Shape type="rectangle" />}
      </View>
    </Layout>
  );
};

export { ReturnConfirmation };
